import React, { useState } from 'react';
import styles from './ORSMDashboard.module.scss';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import Text from '../../../../Components/Base/Text/Text';
import ORSMSubscriptionPayments from './ORSMSubscriptionPayments';
import useNotification from '../../../../Hooks/useNotification.hook';
import { useDisclose } from '../../../Modal/Hooks/useDisclose';
import ORSMDomainChangeModal from '../DomainChange/ORSMDomainChangeModal';
import ORSMFaqs from '../FAQS/ORSMFaqs';
import ORSMSettingsWorkplaceSelectionModal from '../Settings/ORSMSettingsWorkplaceSelectionModal';
import {
	SubscriptionMembership,
	useSubscriptionMembershipCancelMutation,
	useWorkplacesByRootQuery,
	Workplace,
} from '../../../../graphql/graphql';
import Loading from '../../../../Components/Loading';
import { ORSMWorkplaceSettingsType } from '../Settings/ORSMWorkplaceSettings';
import ORSMWorkplaceSettingsModal from '../Settings/ORSMWorkplaceSettingsModal';
import RemoveButton from '../../../../Components/Button/RemoveButton';
import NavigationCard from '../../../../Components/Base/NavigationCard/NavigationCard';
import useTranslation from '../../../../Hooks/useTranslation';
import { getMomentDate } from '../../../../Utils/dates.util';
import { useAppContext } from '../../../../Services/AppContext';
import useApiClient from '../../../Base/Hooks/useApiClient';

interface Props {
	slug: string;
	rootWorkplaceId: string;
	onDomainChange: () => void;
	onMembershipChange: () => void;
	activeMembership?: SubscriptionMembership;
}

const ORSMDashboard: React.FC<Props> = ({
	slug,
	rootWorkplaceId,
	onDomainChange,
	onMembershipChange,
	activeMembership,
}) => {
	const {
		data: workplacesData,
		isLoading,
		refetch,
	} = useWorkplacesByRootQuery({
		rootWorkplaceId,
	});
	const workplaces: Workplace[] =
		(workplacesData?.workplaces?.data as Workplace[]) ?? [];
	const [selectedWorkplace, setSelectedWorkplace] = useState<
		Workplace | undefined
	>(undefined);
	const [selectedSettingsType, setSelectedSettingsType] = useState<
		ORSMWorkplaceSettingsType | undefined
	>(undefined);
	const { isOpen, onOpen, onClose } = useDisclose(false);
	const { showNotificationSuccess } = useNotification();
	const {
		isOpen: isWorkplaceSelectionOpen,
		onOpen: onOpenWorkplaceSelection,
		onClose: onCloseWorkplaceSelection,
	} = useDisclose(false);
	const {
		isOpen: isWorkplaceSettingsOpen,
		onOpen: onOpenWorkplaceSettings,
		onClose: onCloseWorkplaceSettings,
	} = useDisclose(false);
	const { t } = useTranslation();
	const [cancelSubscription] = useSubscriptionMembershipCancelMutation();
	const { dateFormatMoment, isProduction } = useAppContext();
	const { displayError, displayGQLError } = useApiClient();

	let domain = `https://${slug}.meddi.com`;

	if (!isProduction) {
		domain = `https://${slug}.meddi-devcal.cz`;
	}

	const handleCopyDomain = () => {
		navigator.clipboard.writeText(domain);
		showNotificationSuccess({
			content: 'component.ORSMDashboard.savedToClipboard',
		});
	};

	const handleSelectWorkplace = (workplace: Workplace) => {
		setSelectedWorkplace(workplace);
		onCloseWorkplaceSelection();
		onOpenWorkplaceSettings();
	};

	const handleOpenSettings = (type: ORSMWorkplaceSettingsType) => {
		setSelectedSettingsType(type);
		if (workplaces.length > 1) {
			onOpenWorkplaceSelection();
		} else {
			handleSelectWorkplace(workplaces[0]);
		}
	};

	const handleCloseSettings = () => {
		setSelectedWorkplace(undefined);
		setSelectedSettingsType(undefined);
		onCloseWorkplaceSettings();
	};

	const handleCancelSubscription = async () => {
		if (!activeMembership) return;
		try {
			const result = await cancelSubscription({
				input: { id: activeMembership.id },
			});
			displayGQLError(result);
			if ('data' in result) {
				onMembershipChange();
			}
		} catch (e) {
			displayError(e);
		}
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<>
			<div className={styles.wrapper}>
				<div>
					<Text
						className={styles.pageTitle}
						tx='component.ORSMDashboard.pageTitle'
					/>
					<Text tx='component.ORSMDashboard.pageSubtitle' />
				</div>
				<div className={styles.main}>
					<div className={styles.domainBlock}>
						<div className={styles.header}>
							<div className={styles.title}>
								<img
									src='/dist/icons/subscription/globe-regular-full.svg'
									alt='globe icon'
								/>{' '}
								{t('component.ORSMDashboard.domainConfiguration')}
							</div>
							<LinkButton
								title='component.ORSMDashboard.changeDomain'
								theme={Theme.BLUE}
								onClick={onOpen}
							/>
						</div>
						<div>
							<Text
								tx='component.ORSMDashboard.linkForYourSystem'
								className={styles.title}
							/>
							<div className={styles.address}>
								<a href={domain} target='_blank'>
									{domain}
								</a>
								<div className={styles.copyButton} onClick={handleCopyDomain}>
									<img
										src='/dist/icons/subscription/copy-regular-full.svg'
										alt='coppy icon'
									/>
									{t('component.ORSMDashboard.copy')}
								</div>
							</div>
						</div>
						<Text
							tx='component.ORSMDashboard.linkManual'
							className={styles.hint}
						/>
					</div>
					<div className={styles.speedButtons}>
						<NavigationCard
							icon='/dist/icons/subscription/notes-regular-full.svg'
							title='component.ORSMDashboard.setupCaseTypes'
							subtitle='component.ORSMDashboard.definePricesAndTimes'
							onClick={() =>
								handleOpenSettings(ORSMWorkplaceSettingsType.CASE_TYPES)
							}
						/>
						<NavigationCard
							icon='/dist/icons/subscription/calendar-clock-regular-full.svg'
							title='component.ORSMDashboard.setupTimeForOnlineOrdering'
							subtitle='component.ORSMDashboard.defineWhenCanOrder'
							onClick={() =>
								handleOpenSettings(ORSMWorkplaceSettingsType.ONLINE_ORDERING)
							}
						/>
						<NavigationCard
							icon='/dist/icons/subscription/calendar-circle-user-regular-full.svg'
							title='component.ORSMDashboard.setupShifts'
							subtitle='component.ORSMDashboard.defineWhoAndWhenAssign'
							onClick={() =>
								handleOpenSettings(ORSMWorkplaceSettingsType.SHIFTS)
							}
						/>
						<NavigationCard
							icon='/dist/icons/subscription/shield-keyhole-regular-full.svg'
							title='component.ORSMDashboard.setupRestrictions'
							subtitle='component.ORSMDashboard.setupWhoCanOrder'
							onClick={() =>
								handleOpenSettings(ORSMWorkplaceSettingsType.RESTRICTION)
							}
						/>
					</div>
				</div>
				<div className={styles.subscriptionPayments}>
					<div className={styles.header}>
						<div className={styles.title}>
							<img
								src='/dist/icons/subscription/circle-dollar-regular-full.svg'
								alt='globe icon'
							/>{' '}
							{t('component.ORSMDashboard.paymentForSubscription')}
						</div>
						{activeMembership && (
							<div className={styles.right}>
								{activeMembership.cancelledAt ? (
									<Text
										tx='component.ORSMDashboard.systemWillBeActiveUntil'
										txParams={{
											date: getMomentDate(activeMembership.activeTo).format(
												dateFormatMoment,
											),
										}}
										className={styles.nextPayment}
									/>
								) : (
									<>
										<Text
											tx='component.ORSMDashboard.nextPaymentWillBeCharged'
											txParams={{
												date: getMomentDate(activeMembership.activeTo).format(
													dateFormatMoment,
												),
											}}
											className={styles.nextPayment}
										/>
										<RemoveButton
											onClick={handleCancelSubscription}
											title='component.ORSMDashboard.cancelSubscription'
											confirmationTitle='component.ORSMDashboard.cancelSubscription'
											confirmationText='component.ORSMDashboard.cancelSubscriptionConfirmationText'
											confirmationNote='component.ORSMDashboard.cancelSubscriptionConfirmationNote'
											flashMessage='component.ORSMDashboard.cancelSubscriptionFlashMessage'
										/>
									</>
								)}
							</div>
						)}
					</div>
					<ORSMSubscriptionPayments workplaceId={rootWorkplaceId} />
				</div>
				<div className={styles.faqsWrapper}>
					<ORSMFaqs />
				</div>
				{isOpen && (
					<ORSMDomainChangeModal
						isOpen
						onClose={onClose}
						rootWorkplaceId={rootWorkplaceId}
						onChange={() => {
							onClose();
							onDomainChange();
						}}
					/>
				)}
			</div>
			{isWorkplaceSelectionOpen && workplaces && (
				<ORSMSettingsWorkplaceSelectionModal
					isOpen
					workplaces={workplaces}
					onClose={onCloseWorkplaceSelection}
					onSelect={handleSelectWorkplace}
				/>
			)}
			{isWorkplaceSettingsOpen && selectedSettingsType && selectedWorkplace && (
				<ORSMWorkplaceSettingsModal
					type={selectedSettingsType}
					isOpen
					onClose={handleCloseSettings}
					workplace={selectedWorkplace}
					changeType={type => setSelectedSettingsType(type)}
					onWorkplaceUpdate={async () => {
						await refetch();
					}}
				/>
			)}
		</>
	);
};

export default ORSMDashboard;
